import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Users, CalendarCheck, Pill, X } from 'lucide-react';

const tipiVisitaLabels = {
  prelievo_ematico: 'Prelievo Ematico', visita_cardiologica: 'Visita Cardiologica',
  visita_ortopedica: 'Visita Ortopedica', visita_neurologica: 'Visita Neurologica',
  visita_dermatologica: 'Visita Dermatologica', visita_oculistica: 'Visita Oculistica',
  controllo_mmg: 'Controllo MMG', esame_diagnostico: 'Esame Diagnostico',
  ecografia: 'Ecografia', radiografia: 'Radiografia', tac: 'TAC',
  fisioterapia: 'Fisioterapia', altro: 'Altro',
};

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults(null); setOpen(false); return; }
    setLoading(true);
    try {
      const res = await api.get('/ricerca-globale', { params: { q } });
      setResults(res.data);
      const hasResults = (res.data.ospiti?.length || 0) + (res.data.appuntamenti?.length || 0) + (res.data.terapie?.length || 0) > 0;
      setOpen(hasResults || q.length >= 2);
    } catch { setResults(null); }
    setLoading(false);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 300);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setQuery('');
    setOpen(false);
    setResults(null);
  };

  const handleClear = () => {
    setQuery('');
    setOpen(false);
    setResults(null);
    inputRef.current?.focus();
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keyboard shortcut: Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const totalResults = results ? (results.ospiti?.length || 0) + (results.appuntamenti?.length || 0) + (results.terapie?.length || 0) : 0;

  return (
    <div ref={containerRef} className="relative hidden lg:block" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          ref={inputRef}
          value={query}
          onChange={handleChange}
          onFocus={() => { if (results && totalResults > 0) setOpen(true); }}
          placeholder="Cerca ospite, farmaco, visita... (Ctrl+K)"
          className="pl-9 pr-8 h-9 w-72 text-sm bg-slate-50 border-slate-200 focus:bg-white"
          data-testid="global-search-input"
        />
        {query && (
          <button onClick={handleClear} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full mt-1.5 left-0 w-96 bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="global-search-results">
          {loading && <div className="px-4 py-3 text-xs text-slate-400">Ricerca...</div>}

          {results && totalResults === 0 && !loading && (
            <div className="px-4 py-6 text-center">
              <Search className="w-6 h-6 text-slate-300 mx-auto mb-1" />
              <p className="text-xs text-slate-400">Nessun risultato per "{query}"</p>
            </div>
          )}

          {results?.ospiti?.length > 0 && (
            <div>
              <div className="px-3 py-1.5 bg-slate-50 border-b flex items-center gap-1.5">
                <Users className="w-3 h-3 text-blue-500" />
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Ospiti</span>
                <Badge variant="outline" className="text-[9px] ml-auto">{results.ospiti.length}</Badge>
              </div>
              {results.ospiti.map(o => (
                <button
                  key={o.id}
                  onClick={() => handleNavigate(`/ospiti/${o.id}`)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors flex items-center gap-2 border-b border-slate-50"
                  data-testid={`search-result-ospite-${o.id}`}
                >
                  <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-bold text-brand">{o.cognome?.[0]}{o.nome?.[0]}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{o.cognome} {o.nome}</p>
                    <p className="text-[10px] text-slate-400">Camera {o.stanza}/{o.letto} {o.area && `- ${o.area}`}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {results?.appuntamenti?.length > 0 && (
            <div>
              <div className="px-3 py-1.5 bg-slate-50 border-b flex items-center gap-1.5">
                <CalendarCheck className="w-3 h-3 text-emerald-500" />
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Appuntamenti</span>
                <Badge variant="outline" className="text-[9px] ml-auto">{results.appuntamenti.length}</Badge>
              </div>
              {results.appuntamenti.map(a => (
                <button
                  key={a.id}
                  onClick={() => handleNavigate(`/ospiti/${a.ospite_id}`)}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 transition-colors flex items-center gap-2 border-b border-slate-50"
                  data-testid={`search-result-app-${a.id}`}
                >
                  <CalendarCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{a.ospite_nome} - {tipiVisitaLabels[a.tipo] || a.tipo}</p>
                    <p className="text-[10px] text-slate-400">
                      {new Date(a.data_ora).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      {a.specialista && ` - ${a.specialista}`}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {results?.terapie?.length > 0 && (
            <div>
              <div className="px-3 py-1.5 bg-slate-50 border-b flex items-center gap-1.5">
                <Pill className="w-3 h-3 text-amber-500" />
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Terapie</span>
                <Badge variant="outline" className="text-[9px] ml-auto">{results.terapie.length}</Badge>
              </div>
              {results.terapie.map(t => (
                <button
                  key={t.id}
                  onClick={() => handleNavigate(`/ospiti/${t.ospite_id}`)}
                  className="w-full text-left px-3 py-2 hover:bg-amber-50 transition-colors flex items-center gap-2 border-b border-slate-50"
                  data-testid={`search-result-terapia-${t.id}`}
                >
                  <Pill className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{t.farmaco} {t.dosaggio}</p>
                    <p className="text-[10px] text-slate-400">{t.ospite_nome} {t.principio_attivo && `- ${t.principio_attivo}`}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
